// ---- Castle ----
// A walkable stone keep, now three storeys tall and divided into rooms by stone
// arcades. Authored in local coords centered on the footprint (feet at y=0), gate
// facing south (+z):
//
//   - Ground floor: a flagstone hall split by an arcade into a GREAT HALL (north,
//     the throne room) and a DINING HALL / entrance (south, where you walk in
//     through the gateway). The east bay carries the grand staircase up.
//   - Second floor (y=FLOOR2): reached by the east stair. Split by a matching
//     arcade into a LIBRARY/SOLAR (north) and a BEDCHAMBER (south). A west stair
//     climbs on from the bedchamber to the roof.
//   - Third floor (y=FLOOR3): an open crenellated rampart you patrol — the King
//     walks here. Open to the sky.
//   - Four round corner towers with conical roofs rise above the parapet.
//
// Walls are full-height stone with a procedural cobblestone veneer + leaded
// windows on the ground and second floors; floors are paved with flagstones. The
// cutaway system (driven by the walkable floors) keeps the player visible inside.

import { defineStructure } from "./define.ts";
import { shade, rand } from "../color.ts";
import {
  rug, throne, banquetTable, bench, bookshelf, chandelier,
  tapestry, wallCrest, barrel, crate,
  fourPosterBed, chest, roundTable, fireplace,
} from "./furniture/index.ts";

// --- dimensions (world units) ---
const HALF = 15;          // interior half-extent (interior spans -HALF..HALF)
const WT = 1.4;           // wall thickness
const OUT = HALF + WT;    // outer face of the walls (16.4)
const FLOOR2 = 5;         // height of the second floor
const FLOOR3 = 10;        // height of the third floor (rampart)
const TOP = FLOOR3 + 2.4; // top of the parapet (12.4)
const GATE_HW = 2;        // gateway half-width
const GATE_H = 3.6;       // gateway opening height
// How far the flagstone paving stands proud of the floor slab it's laid on (the
// tiles' top relative to the slab top). The walkable surface is registered here,
// at the top of the visible paving, so characters stand ON the flagstones rather
// than sinking through them to the bare slab. Keep in sync with flagstones().
const FLAG_RISE = 0.09;

// --- staircases (footprints against the side walls) ---
// East stair: ground (y=0) → second floor (y=FLOOR2). Climbs south→north.
const SA_X0 = 11.5, SA_X1 = HALF;
const SA_ZT = -7, SA_ZB = 9; // top (north, high) / bottom (south, low)
// West stair: second floor (y=FLOOR2) → roof (y=FLOOR3). Climbs south→north.
const SB_X0 = -HALF, SB_X1 = -11.5;
const SB_ZT = -7, SB_ZB = 9;

// --- palette ---
const STONE = 0x9a948c;
const STONE_LT = shade(STONE, 0.08);
const STONE_DK = shade(STONE, -0.14);
const FLOOR_COL = shade(STONE, -0.06);
const MORTAR = shade(STONE, -0.24); // dark recessed bed behind the cobbles
const GLASS = 0x2b3c57;             // cool leaded glass
const SLATE = 0x4a5568;             // tower roofs
const BANNER = 0xb23a48;            // deep heraldic red pennants
const CLOTH_BLUE = 0x2f4a8c;        // tapestry cloths
const CLOTH_GREEN = 0x2f6b45;
const GOLD_ACCENT = 0xc9a23a;       // heraldic gold trim/emblems

// A window's opening centres (ground + second floor).
const WY1 = 2.7;             // ground-floor window centre height
const WY2 = FLOOR2 + 2.5;    // second-floor window centre height (7.5)
type Win = { at: number; cy: number };
// Two stacked windows (ground + 2F) at each given along-wall position.
const stacked = (ats: number[]): Win[] =>
  ats.flatMap((a) => [{ at: a, cy: WY1 }, { at: a, cy: WY2 }]);

export default defineStructure({
  type: "castle",
  mapColor: STONE,
  build: (c) => {
    const { wall, collideBox, floorSlab, staircase, crenellate, tower, light, dot, box, cylinder, shell, keep, noShadow } = c;

    // --- procedural cobblestone veneer ---------------------------------------
    // Tile shade-varied stone blocks over a wall FACE so the flat wall reads as
    // coursed cobblestone: a dark "mortar bed" panel, then lighter blocks laid
    // proud of it with brick-stagger and per-block colour jitter (deterministic,
    // so every client sees the same masonry). Purely visual — the wall collider
    // already covers it. `axis` is the run direction; the face sits at
    // `faceCoord` on the other horizontal axis, protruding toward `+faceDir`.
    // `holes` (along-centre + half-extents) are skipped so windows aren't paved.
    type Hole = { c: number; hw: number; y: number; hh: number };
    const masonry = (
      axis: "x" | "z",
      run0: number, run1: number,
      faceCoord: number, faceDir: 1 | -1,
      baseY: number, topY: number,
      holes: Hole[] = [],
    ) => {
      const place = (a: number, y: number, fc: number, wx: number, hy: number, dz: number, col: number) =>
        axis === "x" ? box(a, y, fc, wx, hy, dz, col) : box(fc, y, a, dz, hy, wx, col);
      // dark mortar bed across the whole face
      place((run0 + run1) / 2, (baseY + topY) / 2, faceCoord + faceDir * 0.05,
        run1 - run0, topY - baseY, 0.1, MORTAR);
      // coursed blocks
      const runLen = run1 - run0, wallH = topY - baseY;
      const cols = Math.max(1, Math.round(runLen / 1.7));
      const rows = Math.max(1, Math.round(wallH / 0.9));
      const cw = runLen / cols, ch = wallH / rows, gap = 0.12;
      const fz = faceCoord + faceDir * 0.15;
      for (let r = 0; r < rows; r++) {
        const y = baseY + (r + 0.5) * ch;
        const offset = (r % 2) * cw * 0.5; // every other course shifts half a block
        for (let i = -1; i < cols; i++) {
          const a = run0 + (i + 0.5) * cw + offset;
          const w = cw - gap;
          if (a - w / 2 < run0 - 0.01 || a + w / 2 > run1 + 0.01) continue; // don't overhang corners
          if (holes.some((h) => Math.abs(a - h.c) < h.hw && Math.abs(y - h.y) < h.hh)) continue;
          const col = shade(STONE, (rand(a * 7.3 + y * 13.1 + faceCoord * 3.7) - 0.5) * 0.18);
          place(a, y, fz, w, ch - gap, 0.14, col);
        }
      }
    };

    // A castle window: stone frame + sill proud of the wall, recessed leaded
    // glass with a cross-mullion, centred at height `cy`. Set on a wall face.
    const WIN_W = 1.7, WIN_H = 2.6;
    const windowAt = (axis: "x" | "z", along: number, faceCoord: number, faceDir: 1 | -1, cy: number) => {
      const place = (a: number, y: number, fc: number, wx: number, hy: number, dz: number, col: number) =>
        axis === "x" ? box(a, y, fc, wx, hy, dz, col) : box(fc, y, a, dz, hy, wx, col);
      const fr = faceCoord + faceDir * 0.2;    // frame proud of the wall
      const glassC = faceCoord + faceDir * 0.08; // glass recessed behind it
      const t = 0.2, w = WIN_W, h = WIN_H;
      // Glass doesn't cast shadow — torchlight leaks out through the pane.
      noShadow(() => place(along, cy, glassC, w, h, 0.06, GLASS));
      place(along, cy + h / 2 + t / 2, fr, w + 2 * t, t, 0.34, STONE_LT);       // lintel
      place(along, cy - h / 2 - t / 2, fr, w + 2 * t, t * 1.3, 0.42, STONE_LT); // sill
      place(along - w / 2 - t / 2, cy, fr, t, h + 2 * t, 0.34, STONE_LT);       // left jamb
      place(along + w / 2 + t / 2, cy, fr, t, h + 2 * t, 0.34, STONE_LT);       // right jamb
      place(along, cy, fr - faceDir * 0.06, 0.12, h, 0.24, STONE_DK);          // vertical mullion
      place(along, cy, fr - faceDir * 0.06, w, 0.12, 0.24, STONE_DK);          // horizontal mullion
    };

    // Veneer a wall face with cobbles, punching window recesses for `wins`.
    const facade = (
      axis: "x" | "z", run0: number, run1: number,
      faceCoord: number, faceDir: 1 | -1, wins: Win[] = [],
    ) => {
      const holes: Hole[] = wins.map((w) => ({ c: w.at, hw: WIN_W / 2 + 0.45, y: w.cy, hh: WIN_H / 2 + 0.55 }));
      masonry(axis, run0, run1, faceCoord, faceDir, 0, TOP, holes);
      for (const w of wins) windowAt(axis, w.at, faceCoord, faceDir, w.cy);
    };

    // Veneer an INTERIOR wall face with cobbles (no glass — windows live on the
    // outer face). Same masonry, but tagged with the OUTER normal of the wall it
    // skins so the inner stone fades together with that wall under the cutaway.
    const innerFacade = (
      axis: "x" | "z", run0: number, run1: number,
      faceCoord: number, faceDir: 1 | -1, nx: number, nz: number,
      wins: Win[] = [],
    ) => {
      const holes: Hole[] = wins.map((w) => ({ c: w.at, hw: WIN_W / 2 + 0.45, y: w.cy, hh: WIN_H / 2 + 0.55 }));
      shell(nx, nz, () => masonry(axis, run0, run1, faceCoord, faceDir, 0, TOP, holes));
    };

    // ---- procedural flagstone floor -----------------------------------------
    // Lay shade-varied stone slabs proud of a floor slab so it reads as a paved
    // flagstone floor rather than a flat plane: coursed tiles with a brick-stagger
    // and per-tile colour jitter (deterministic), the darker base slab showing
    // through the joints as grout. Purely visual; the platform surface is the slab.
    const flagstones = (minX: number, maxX: number, minZ: number, maxZ: number, y: number) => {
      const w = maxX - minX, d = maxZ - minZ;
      const cols = Math.max(1, Math.round(w / 2.4));
      const rows = Math.max(1, Math.round(d / 2.4));
      const tw = w / cols, td = d / rows, gap = 0.22;
      for (let r = 0; r < rows; r++) {
        const cz = minZ + (r + 0.5) * td;
        const offset = (r % 2) * tw * 0.5; // stagger alternate courses
        for (let i = -1; i < cols; i++) {
          const cx = minX + (i + 0.5) * tw + offset;
          if (cx - tw / 2 < minX - 0.01 || cx + tw / 2 > maxX + 0.01) continue; // no overhang
          const col = shade(FLOOR_COL, 0.05 + (rand(cx * 5.1 + cz * 9.7 + y * 2.3) - 0.5) * 0.16);
          box(cx, y + 0.03, cz, tw - gap, 0.12, td - gap, col);
        }
      }
    };

    // ---- room-dividing arcade -----------------------------------------------
    // A row of square stone pillars on an architrave beam, running east-west at
    // z=`zc` from x0..x1, splitting a floor into two rooms. The bays between
    // pillars are OPEN passages (you walk through), so it reads as a threshold
    // between chambers without ever hiding the player — the openings keep the
    // sightline clear, unlike a solid partition. Pillars carry colliders; the
    // high beam is purely visual.
    const arcadeEW = (zc: number, xs: number[], x0: number, x1: number, baseY: number) => {
      const springY = baseY + 3.6;     // top of the openings / underside of the beam
      keep(() => {
        box((x0 + x1) / 2, springY + 0.22, zc, x1 - x0, 0.44, 0.8, STONE_LT); // architrave
        for (const px of xs) {
          box(px, baseY + (springY - baseY) / 2, zc, 0.82, springY - baseY, 0.82, STONE); // shaft
          box(px, baseY + 0.13, zc, 1.12, 0.26, 1.12, STONE_DK);              // base
          box(px, springY - 0.13, zc, 1.12, 0.26, 1.12, STONE_LT);            // capital
          // small corbels suggesting the arch springing from each capital
          for (const sx of [-1, 1]) box(px + sx * 0.62, springY - 0.05, zc, 0.5, 0.28, 0.6, STONE_LT);
        }
      });
      for (const px of xs) collideBox(px - 0.5, px + 0.5, zc - 0.5, zc + 0.5, baseY, springY);
    };

    // ===========================================================================
    // GROUND FLOOR
    // ===========================================================================
    // Extend the slab out to the OUTER wall face so the floor tucks under the
    // walls — no seam between the floor edge and the wall base.
    floorSlab(-OUT, OUT, -OUT, OUT, 0, 0.5, FLOOR_COL, FLAG_RISE);
    flagstones(-HALF, HALF, -HALF, HALF, 0);
    flagstones(-GATE_HW, GATE_HW, HALF, OUT, 0); // gateway threshold
    box(0, -0.95, 0, OUT * 2 + 1, 0.7, OUT * 2 + 1, STONE_DK); // foundation apron

    // ---- perimeter walls (full height — block all floors) ----
    wall(-OUT, OUT, -OUT, -HALF, 0, TOP, STONE);            // north
    crenellate(-OUT, OUT, -OUT, -HALF, TOP, STONE_LT, "x");
    wall(-OUT, -HALF, -OUT, OUT, 0, TOP, STONE);            // west
    crenellate(-OUT, -HALF, -OUT, OUT, TOP, STONE_LT, "z");
    wall(HALF, OUT, -OUT, OUT, 0, TOP, STONE);              // east
    crenellate(HALF, OUT, -OUT, OUT, TOP, STONE_LT, "z");

    // ---- south wall with gateway ----
    wall(-OUT, -GATE_HW, HALF, OUT, 0, TOP, STONE);          // left flank
    crenellate(-OUT, -GATE_HW, HALF, OUT, TOP, STONE_LT, "x");
    wall(GATE_HW, OUT, HALF, OUT, 0, TOP, STONE);            // right flank
    crenellate(GATE_HW, OUT, HALF, OUT, TOP, STONE_LT, "x");
    // Wall above the gateway (visual), and a collider blocking the gap on every
    // floor above the opening so the upper ramparts stay enclosed.
    box(0, (GATE_H + TOP) / 2, (HALF + OUT) / 2, GATE_HW * 2, TOP - GATE_H, WT, STONE);
    crenellate(-GATE_HW, GATE_HW, HALF, OUT, TOP, STONE_LT, "x");
    collideBox(-GATE_HW, GATE_HW, HALF, OUT, GATE_H, TOP);
    // Arched jambs framing the doorway — kept always-visible by the cutaway.
    // Stand the frame clearly PROUD of the cobble veneer (front at OUT+0.4) with
    // its back tucked inside the wall, and let the jambs reach a touch INTO the
    // opening. That way no frame face is coplanar with the wall flanks or the
    // masonry mortar bed — otherwise the frame z-fights the wall (flickers).
    keep(() => {
      const fd = 1.6;            // frame depth
      const fz = OUT + 0.4 - fd / 2;  // centre: front face proud of the veneer, back inside the wall
      const inset = 0.1;         // jambs protrude slightly into the opening
      box(-(GATE_HW - inset) - 0.2, GATE_H / 2, fz, 0.4, GATE_H, fd, STONE_DK);
      box((GATE_HW - inset) + 0.2, GATE_H / 2, fz, 0.4, GATE_H, fd, STONE_DK);
      box(0, GATE_H + 0.2, fz, GATE_HW * 2 + 0.8, 0.4, fd, STONE_DK);
    });

    // ---- cobblestone veneer + windows on the exterior faces ----
    shell(0, -1, () => facade("x", -OUT, OUT, -OUT, -1, stacked([-9, 9])));   // north
    shell(-1, 0, () => facade("z", -OUT, OUT, -OUT, -1, stacked([-9, 9])));   // west
    shell(1, 0, () => facade("z", -OUT, OUT, OUT, 1, stacked([-9, 9])));      // east
    shell(0, 1, () => facade("x", -OUT, -GATE_HW, OUT, 1, stacked([-9])));    // south left
    shell(0, 1, () => facade("x", GATE_HW, OUT, OUT, 1, stacked([9])));       // south right

    // ---- cobblestone veneer on the INTERIOR wall faces ----
    innerFacade("x", -HALF, HALF, -HALF, 1, 0, -1, stacked([-9, 9]));   // north inner
    innerFacade("z", -HALF, HALF, -HALF, 1, -1, 0, stacked([-9, 9]));   // west inner
    innerFacade("z", -HALF, HALF, HALF, -1, 1, 0, stacked([-9, 9]));    // east inner
    innerFacade("x", -HALF, -GATE_HW, HALF, -1, 0, 1, stacked([-9]));   // south-left inner
    innerFacade("x", GATE_HW, HALF, HALF, -1, 0, 1, stacked([9]));      // south-right inner

    // ---- ground-floor arcade: great hall (north) | dining hall (south) ----
    arcadeEW(-1, [-11, -4, 4, 11], -HALF, SA_X0, 0);

    // ===========================================================================
    // SECOND FLOOR (y=FLOOR2) — minus the east stairwell opening
    // ===========================================================================
    const T = 0.4;
    const F2 = FLOOR2 + FLAG_RISE;
    floorSlab(-HALF, SA_X0, -HALF, HALF, FLOOR2, T, FLOOR_COL, F2);      // west of the stair
    floorSlab(SA_X0, HALF, -HALF, SA_ZT, FLOOR2, T, FLOOR_COL, F2);      // north of the well
    floorSlab(SA_X0, HALF, SA_ZB, HALF, FLOOR2, T, FLOOR_COL, F2);       // south of the well
    flagstones(-HALF, SA_X0, -HALF, HALF, FLOOR2);
    flagstones(SA_X0, HALF, -HALF, SA_ZT, FLOOR2);
    flagstones(SA_X0, HALF, SA_ZB, HALF, FLOOR2);

    // east stair (ground → 2F) + railings around its opening (north end open)
    staircase(SA_X0, SA_X1, SA_ZT, SA_ZB, 0, FLOOR2, "z", "max", STONE_LT, 20);
    wall(SA_X0, SA_X0 + 0.2, SA_ZT, SA_ZB, FLOOR2, 1.0, STONE_LT);       // west rail
    wall(SA_X0, SA_X1, SA_ZB - 0.2, SA_ZB, FLOOR2, 1.0, STONE_LT);       // south rail

    // ---- second-floor arcade: library (north) | bedchamber (south) ----
    arcadeEW(-1, [-9.5, -4, 4, 9.5], SB_X1, SA_X0, FLOOR2);

    // ===========================================================================
    // THIRD FLOOR (y=FLOOR3) — open rampart, minus the west stairwell opening
    // ===========================================================================
    const F3 = FLOOR3 + FLAG_RISE;
    floorSlab(SB_X1, HALF, -HALF, HALF, FLOOR3, T, FLOOR_COL, F3);       // east of the stair
    floorSlab(-HALF, SB_X1, -HALF, SB_ZT, FLOOR3, T, FLOOR_COL, F3);     // north of the well
    floorSlab(-HALF, SB_X1, SB_ZB, HALF, FLOOR3, T, FLOOR_COL, F3);      // south of the well
    flagstones(SB_X1, HALF, -HALF, HALF, FLOOR3);
    flagstones(-HALF, SB_X1, -HALF, SB_ZT, FLOOR3);
    flagstones(-HALF, SB_X1, SB_ZB, HALF, FLOOR3);

    // west stair (2F → roof) + railings (north end open onto the rampart)
    staircase(SB_X0, SB_X1, SB_ZT, SB_ZB, FLOOR2, FLOOR3, "z", "max", STONE_LT, 20);
    wall(SB_X1 - 0.2, SB_X1, SB_ZT, SB_ZB, FLOOR3, 1.0, STONE_LT);       // east rail
    wall(SB_X0, SB_X1, SB_ZB - 0.2, SB_ZB, FLOOR3, 1.0, STONE_LT);       // south rail

    // ---- corner towers ----
    const TR = 2.6, TH = 17;
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        const tx = sx * OUT, tz = sz * OUT;
        tower(tx, tz, TR, TH, STONE, SLATE);
        cylinder(tx, TH + 1.7, tz, 0.08, 0.08, 2.4, STONE_DK); // flagpole
        box(tx + 0.78, TH + 3.4, tz, 1.3, 0.85, 0.08, BANNER); // pennant
      }
    }

    // ===========================================================================
    // FURNISHINGS
    // ===========================================================================
    const CEIL1 = FLOOR2 - 0.4;  // underside of the 2F slab (ground-floor ceiling)
    const CEIL2 = FLOOR3 - 0.4;  // underside of the 3F slab (2F ceiling)

    // ---- ground: GREAT HALL (north) ----
    throne(c, 0, -12);
    rug(c, 0, -7, 4.4, 13);
    chandelier(c, 0, -10, CEIL1);
    chandelier(c, 0, -4, CEIL1);
    fireplace(c, HALF, -11.5, 1, 0);                 // east-wall hearth
    tapestry(c, -4.3, 3.2, -HALF, "x", 2.0, 3.2, 0, -1, CLOTH_BLUE, BANNER);
    tapestry(c, 4.3, 3.2, -HALF, "x", 2.0, 3.2, 0, -1, BANNER, GOLD_ACCENT);
    wallCrest(c, 0, 4.1, -HALF, 0, -1, GOLD_ACCENT, BANNER);
    bookshelf(c, -HALF + 0.5, -10, 1);
    bookshelf(c, -HALF + 0.5, -4.5, 1);

    // ---- ground: DINING HALL / entrance (south) ----
    banquetTable(c, -6, 6, 10);
    bench(c, -7.7, 6, 10);                 // west bench faces the table (+x, the default)
    bench(c, -4.3, 6, 10, -Math.PI / 2);   // east bench faces back across it (-x)
    tapestry(c, -HALF, 3.0, 6, "z", 2.0, 3.0, -1, 0, CLOTH_GREEN, GOLD_ACCENT);
    // storeroom + chest by the gate
    barrel(c, -13, 12.7);
    barrel(c, -13.9, 11.8);
    crate(c, -12, 12.8, 1.2);
    crate(c, -13.2, 10.4, 0.9);
    chest(c, 12.5, 12.5);

    // ---- second floor: LIBRARY / SOLAR (north) ----
    roundTable(c, 0, -8, FLOOR2);
    chandelier(c, 0, -8, CEIL2);
    bookshelf(c, -HALF + 0.5, -11, 1, FLOOR2);
    bookshelf(c, HALF - 0.5, -11, -1, FLOOR2);
    tapestry(c, 0, FLOOR2 + 3.0, -HALF, "x", 2.4, 3.2, 0, -1, CLOTH_BLUE, GOLD_ACCENT);

    // ---- second floor: BEDCHAMBER (south) ----
    fourPosterBed(c, -7, 8, FLOOR2);
    chest(c, -7, 11.4, FLOOR2);
    fireplace(c, -HALF, 12, -1, 0, FLOOR2);          // west-wall hearth
    tapestry(c, 5, FLOOR2 + 3.0, HALF, "x", 2.0, 3.0, 0, 1, BANNER, GOLD_ACCENT);

    // ---- third floor: open rampart ----
    light(0, 3.0, HALF - 0.4);          // gateway brazier (lights the entrance)
    // a couple of small heraldic banners on the parapet
    tapestry(c, -6, FLOOR3 + 1.0, -HALF, "x", 1.4, 1.7, 0, -1, BANNER, GOLD_ACCENT);
    tapestry(c, 6, FLOOR3 + 1.0, -HALF, "x", 1.4, 1.7, 0, -1, CLOTH_BLUE, GOLD_ACCENT);

    // ---- minimap footprint ring ----
    for (let i = 0; i < 30; i++) {
      const a = (i / 30) * Math.PI * 2;
      dot(Math.cos(a) * (HALF + 0.6), Math.sin(a) * (HALF + 0.6), STONE);
    }
  },
});
